import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MdbOptionComponent } from 'ng-uikit-pro-standard';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FormElementViewModel, IFormElementViewModel, IFormElementViewModelOptions } from '../forms';
import { CommonService } from '../../services/common.service';
import { IPageDataViewModel } from '../../pages/pages';

@Component({
  selector: 'app-form-auto-complete',
  templateUrl: './form-auto-complete.component.html',
  styleUrls: ['./form-auto-complete.component.scss']
})
export class FormAutoCompleteComponent implements OnInit {

  validationErrors: string[] = [];
    
  text: string = 'Initializing...';
  autoCompleteDisabled: boolean = false;

  private searchText = new Subject<string>();
  results: Observable<IFormAutoCompleteItem[]>;

  noResultsText?: string;

  initializing: boolean = true;

  /// todo... add via view model / input
  inputAutocomplete: string = 'off';

  @Input() vm: IFormAutoCompleteViewModel;

  @Output() selected = new EventEmitter<IFormAutoCompleteItem>();

  /**
   * 
   * @param commonService 
   */
  constructor(private commonService: CommonService) {
  }

  modelChanged(t) {
    //console.log(`model changed... ${t}, ${this.searchText.hasError}`);
    this.searchText.next(t);
  }

  /**
   * 
   * @param searchString
   * @returns 
   */
  private autoCompleteSearch(searchString: string): Observable<IFormAutoCompleteItem[]> {
    if (!this.vm)
      return of([]);

    return this.commonService.getData('PageCommand', 'auto-complete', this.vm.apiAction, { searchString: searchString }, this.vm.apiPath).pipe(
      debounceTime(250),
      map((result: IFormCompleteSearchResult) => {
        this.noResultsText = result.workingText ? 'Searching...'
          : result.errorText ? result.errorText
            : result?.data?.errorText ? result?.data?.errorText
              : !result?.data?.items?.length ? this.vm.noResultsText ?? 'No Results'
                : undefined;

        return result?.data?.items ?? [];
      })
    );
  }

  /**
   * 
   */
  cleared(vm: IFormAutoCompleteViewModel) {
    if (!this.vm)
      return;

    this.autoCompleteDisabled = false;
    this.vm.dataSet(undefined);
    this.validate(vm);
  }

  /**
   * 
   * @param o 
   * @returns 
   */
  onDisplayValue(o: any): string | undefined {
    const ret = !o ? ''
      : o.length ? o
        : o.code ? `${o.code} - ${o.name}`
          : '';
    return ret;
  }

  /**
   * 
   * @param vm 
   * @param arg 
   */
  itemSelected(vm: IFormAutoCompleteViewModel, arg: { text: any, element: MdbOptionComponent }) {
    if (!this.vm)
      return;  
    if (!arg?.text?.code)
      return;

    vm.dataSet(arg.text.code);
    this.validate(vm);
    this.selected.next({ code: arg.text.code, name: arg.text.name });

    this.autoCompleteDisabled = true;
  }

  /**
   * 
   * @param vm 
   */
  private validate(vm: IFormAutoCompleteViewModel) {
    this.validationErrors = !this.vm ? [ 'missing view model' ] : this.vm.validate(vm);
  }

  /**
   * 
   */
  ngOnInit(): void {

    if (!this.vm) {
      this.validationErrors = [ 'no view model' ];
      return;
    }

    // wire up results
    this.results = this.searchText.pipe(
      filter((st: any) => !st?.name && !this.vm.isLocked),
      switchMap((st: string) => {
        if (st?.length > 2)
          return this.autoCompleteSearch(st);

        this.noResultsText = this.vm.noResultsText ?? 'No Results';
        return of([]);
      })
    );    
      
    const code = this.vm.dataGet();

    if (!code) {
      this.initializing = false;
      this.text = '';
      this.validate(this.vm);

    } else {
      const s = this.commonService.getData('PageCommand', this.vm.apiCode ?? 'auto-complete', this.vm.apiAction, { singleCode: code }, this.vm.apiPath, true).pipe(
        tap((result: IFormCompleteSearchResult) => {
          const errorText = result.errorText ?? result.data?.errorText;
          if (errorText) {
            this.validationErrors = [errorText];
            this.text = '!! error !!';

          } else {
            this.initializing = false;
            this.text = result.data?.items?.length > 0 ? this.onDisplayValue(result.data.items[0]) : '';

            this.autoCompleteDisabled = this.text.length ? true : false;
            this.validate(this.vm);
          }
        }),
        take(1)
      ).subscribe(); // use subscribe here cuz not sure if async / await should be used in ngOnInit function
    }

  }


  /**
   * 
   * @param context 
   * @param field 
   * @param action 
   * @param apiPath 
   * @param options 
   * @returns 
   */
  public static viewModel(context: any, field: string, options: IFormAutoCompleteOptions, elementInstance: number = undefined): IFormAutoCompleteViewModel {
    const ret: IFormAutoCompleteViewModel = {
      ...FormElementViewModel.create(context, field, options, elementInstance),

      validate: (vm: IFormAutoCompleteViewModel) => {
        const errors: string[] = [
          vm.isRequired && (vm.dataGet() ?? "").length == 0 ? 'Required' : undefined
        ];
        return errors.filter(e => e);
      }

    };
    return ret;
  }

}

/**
 * 
 */
interface IFormCompleteSearchResult extends IPageDataViewModel {
  data: {
    status: string;
    errorText: string;
    items: IFormAutoCompleteItem[];
  }
}

/**
 * 
 */
export interface IFormAutoCompleteViewModel extends IFormElementViewModel, IFormAutoCompleteOptions {

}

/**
 * 
 */
export interface IFormAutoCompleteItem {
  code: string;
  name: string;
}

/**
 * 
 */
export interface IFormAutoCompleteOptions extends IFormElementViewModelOptions {
  apiPath: string;
  apiCode?: string;
  apiAction: string;

  noResultsText?: string;
}

